import { compareDecimalStrings } from "@/shared/utils/decimal";

import {
  PaymentFeeKind,
  type FeeRowStatus,
  type PaymentFee,
  type ProviderRailRoute,
} from "../types/paymentFees.types";
import {
  computeFee,
  feeQueryFor,
  feeScopeOf,
  feeStatusOf,
  isZeroPrice,
  resolveFee,
  routesCoveredBy,
  scopeKey,
} from "./feeMath";

export type FeeChangeTrend = "NEW" | "UP" | "DOWN" | "SAME";

/** Which price a change belongs to, as an operator reads it. */
export type FeeChangeRole = "GENERAL" | "SPECIAL" | "COST";

export interface FeeChange {
  fee: PaymentFee;
  role: FeeChangeRole;
  status: FeeRowStatus;
  /** The next version's start (or the row's own end, if earlier) — the API never closes `valid_to`. */
  effectiveUntil: string | null;
  previous: PaymentFee | null;
  /** Compared at `amount`, the example amount of the row's currency. */
  trend: FeeChangeTrend;
  amount: string;
  isZero: boolean;
}

export function roleOf(fee: PaymentFee): FeeChangeRole {
  if (fee.fee_kind === PaymentFeeKind.PROVIDER_COST) return "COST";
  return fee.organization_id ? "SPECIAL" : "GENERAL";
}

function trendOf(previous: PaymentFee | null, fee: PaymentFee, amount: string): FeeChangeTrend {
  if (!previous) return "NEW";
  const before = computeFee(previous, amount)?.total;
  const after = computeFee(fee, amount)?.total;
  if (before == null || after == null) return "SAME";
  const sign = compareDecimalStrings(after, before);
  return sign > 0 ? "UP" : sign < 0 ? "DOWN" : "SAME";
}

/** Every version of ONE scope, newest change first. */
export function scopeHistory(
  rows: PaymentFee[],
  now: number,
  amountFor: (fee: PaymentFee) => string,
): FeeChange[] {
  const ascending = [...rows]
    .filter((fee) => !fee.deleted_at)
    .sort((a, b) => Date.parse(a.valid_from) - Date.parse(b.valid_from) || a.version - b.version);

  return ascending
    .map((fee, index) => {
      const previous = ascending[index - 1] ?? null;
      const next = ascending.slice(index + 1).find((candidate) => candidate.is_active) ?? null;
      const ends = [next?.valid_from, fee.valid_to].filter((value): value is string => value != null);
      const effectiveUntil = ends.length
        ? ends.reduce((earliest, value) => (Date.parse(value) < Date.parse(earliest) ? value : earliest))
        : null;
      const amount = amountFor(fee);
      return {
        fee,
        role: roleOf(fee),
        status: feeStatusOf(fee, ascending, now),
        effectiveUntil,
        previous,
        trend: trendOf(previous, fee, amount),
        amount,
        isZero: isZeroPrice(fee),
      };
    })
    .reverse();
}

function byCreatedDesc(a: FeeChange, b: FeeChange): number {
  return Date.parse(b.fee.created_at) - Date.parse(a.fee.created_at) || b.fee.version - a.fee.version;
}

function groupByScope(fees: PaymentFee[]): PaymentFee[][] {
  const groups = new Map<string, PaymentFee[]>();
  for (const fee of fees) {
    if (fee.deleted_at) continue;
    const key = scopeKey(fee.fee_kind, feeScopeOf(fee));
    groups.set(key, [...(groups.get(key) ?? []), fee]);
  }
  return [...groups.values()];
}

/** The whole price book as a feed, in the order the changes were recorded. */
export function changeFeed(
  fees: PaymentFee[],
  now: number,
  amountFor: (fee: PaymentFee) => string,
): FeeChange[] {
  return groupByScope(fees)
    .flatMap((rows) => scopeHistory(rows, now, amountFor))
    .sort(byCreatedDesc);
}

export interface CorridorFeeChange extends FeeChange {
  /** The change belongs to the rule that prices this corridor today in its role. */
  appliesNow: boolean;
}

/** Changes of every rule that can price this corridor: general commission, the organization's own, cost. */
export function corridorHistory(
  route: ProviderRailRoute,
  organizationId: string | null,
  fees: PaymentFee[],
  now: number,
  amountFor: (fee: PaymentFee) => string,
): CorridorFeeChange[] {
  const winners = new Set(
    [
      resolveFee(feeQueryFor(route, PaymentFeeKind.COLOCA_COMMISSION), fees, now),
      organizationId
        ? resolveFee(feeQueryFor(route, PaymentFeeKind.COLOCA_COMMISSION, organizationId), fees, now)
        : null,
      resolveFee(feeQueryFor(route, PaymentFeeKind.PROVIDER_COST), fees, now),
    ]
      .filter((fee): fee is PaymentFee => fee != null)
      .map((fee) => scopeKey(fee.fee_kind, feeScopeOf(fee))),
  );

  const relevant = fees.filter((fee) => {
    if (fee.organization_id && fee.organization_id !== organizationId) return false;
    return routesCoveredBy(fee.fee_kind, feeScopeOf(fee), [route]).length > 0;
  });

  return groupByScope(relevant)
    .flatMap((rows) => {
      const appliesNow = winners.has(scopeKey(rows[0].fee_kind, feeScopeOf(rows[0])));
      return scopeHistory(rows, now, amountFor).map((change) => ({ ...change, appliesNow }));
    })
    .sort(byCreatedDesc);
}
